import { useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { CheckPicker } from "rsuite";

import { Module } from "../../types/TenantInterfaces";
import FieldUtils from "../../utils/FieldUtils";
import { newToastEvent } from "../../utils/ToastUtil";
import AppBadge from "../../elements/AppBadge";
import useQuickSearchFields from "../../api/api-hooks/modules/useQuickSearchFields";
import AppPageHeader from "../Headers/AppPageHeader";
import EditSaveButton from "../../elements/EditSaveButton";

const QuickSearchFields = ({
  currentModule,
}: {
  currentModule: Module | undefined;
}) => {
  const existingQuickSearchFields = currentModule?.Templates?.QuickSearch;

  const [quickSearchFields, setQuickSearchFields] = useState<string[]>(
    existingQuickSearchFields || []
  );
  const [isEditing, setIsEditing] = useState(false);
  const { updateQuickSearchFields, loading } = useQuickSearchFields();

  const availableFields = useMemo(() => {
    if (!Array.isArray(currentModule?.Fields)) return [];

    return currentModule.Fields.flatMap((field) => {
      if (field.Name === "Remote Systems") return [];

      const subitemOptions = FieldUtils.getSubitemOptions(
        field,
        (name: string, value: string, group: string) => {
          return {
            Name: name,
            APIName: value,
            ...(group ? { Group: group } : {}),
          };
        }
      );

      if (subitemOptions) return subitemOptions;

      return {
        Name: field.Name,
        APIName: field.APIName,
        Group: "Base Fields",
      };
    });
  }, [currentModule?.Fields]);

  useEffect(() => {
    if (!existingQuickSearchFields) return;

    setQuickSearchFields(currentModule?.Templates?.QuickSearch);
  }, [existingQuickSearchFields]);

  let finalQuickSearchFields = [...quickSearchFields];

  if (!finalQuickSearchFields.includes("_RecordName")) {
    finalQuickSearchFields.unshift("_RecordName");
  }
  if (!finalQuickSearchFields.includes("id")) {
    finalQuickSearchFields.unshift("id");
  }

  return (
    <>
      <div className="py-3 mb-3 border-bottom border-lighter">
        <AppPageHeader
          size="sm"
          title={"Quick Search Fields"}
          description={`Select the fields to be considered in related pages search input.`}
          customContent={
            <EditSaveButton
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              onSave={handleSave}
              isSubmitting={loading}
            />
          }
        />
      </div>
      {isEditing ? (
        <CheckPicker
          block
          data={availableFields}
          labelKey="Name"
          valueKey="APIName"
          defaultValue={finalQuickSearchFields}
          disabledItemValues={["_RecordName", "id"]}
          onClean={onCleanData}
          onChange={handleCheckPicker}
          groupBy="Group"
        />
      ) : (
        <Card className="border">
          <CardBody className="p-2">
            {finalQuickSearchFields.map((item, index) => (
              <AppBadge key={index}>{item}</AppBadge>
            ))}
          </CardBody>
        </Card>
      )}
    </>
  );

  function handleSave() {
    if (!currentModule || quickSearchFields === existingQuickSearchFields) {
      setIsEditing(false);
      return;
    }

    updateQuickSearchFields({
      moduleAPIName: currentModule.APIName,
      quickSearchFields: quickSearchFields,
      onSuccess: () => {
        setIsEditing(false);
        newToastEvent("success", "Successfully saved Quick Search Fields");
      },
    });
  }

  function handleCheckPicker(quickSearchFields: string[]) {
    setQuickSearchFields(quickSearchFields);
  }

  function onCleanData() {
    setQuickSearchFields([]);
  }
};

export default QuickSearchFields;
